import { CSSProperties, useEffect, useState } from 'react';
import { Button, Icon } from '@/components/ui';
import useNavigate from '@/hooks/use-navigate';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { extractYouTubeVideoId } from '@/utils/extract-youtube-video-id';
import { resolveHeroGradient } from '../hero-section/hero-gradient-presets';

type TextStyle = {
  text: string;
  color: string;
  fontSize: string;
  fontWeight: string;
};

type VideoHeroSectionProps = {
  label?: TextStyle;
  title: TextStyle;
  subtitle?: TextStyle;
  body?: TextStyle;
  button?: {
    text: string;
    href: string;
    startIcon?: string;
    endIcon?: string;
  };
  videoSrc?: string;
  posterImage?: string;
  overlayGradient?: string;
  minHeight?: number;
  contentPaddingStyle?: CSSProperties;
  headingLevel?: 1 | 2;
};

const fontWeightClass = (fontWeight: string) =>
  cn({
    'font-light': fontWeight === 'light',
    'font-normal': fontWeight === 'normal',
    'font-semibold': fontWeight === 'semi-bold',
    'font-bold': fontWeight === 'bold',
  });

const buildYouTubeBackgroundUrl = (videoId: string) => {
  const params = new URLSearchParams({
    autoplay: '1',
    cc_load_policy: '0',
    controls: '0',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    loop: '1',
    modestbranding: '1',
    mute: '1',
    playsinline: '1',
    playlist: videoId,
    rel: '0',
  });

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
};

const YOUTUBE_BACKGROUND_REVEAL_DELAY_MS = 3000;
const YOUTUBE_BACKGROUND_SCALE = 1.18;

const VideoHeroSection = ({
  label,
  title,
  subtitle,
  body,
  button,
  videoSrc,
  posterImage,
  overlayGradient,
  minHeight = 640,
  contentPaddingStyle,
  headingLevel = 1,
}: VideoHeroSectionProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const navigate = useNavigate();
  const resolvedGradient = resolveHeroGradient(overlayGradient);
  const buttonHref = button?.href || '/';
  const youtubeVideoId = extractYouTubeVideoId(videoSrc);
  const TitleTag = headingLevel === 1 ? 'h1' : 'h2';
  const directVideoSrc = youtubeVideoId ? undefined : videoSrc;
  const youtubeIframeWidth = `max(100%, ${((minHeight * 16) / 9).toFixed(2)}px)`;
  const [youtubeIframeLoaded, setYoutubeIframeLoaded] = useState(false);
  const [showYoutubeLoadCover, setShowYoutubeLoadCover] = useState(Boolean(youtubeVideoId));

  useEffect(() => {
    if (!button?.text) return;
    navigate.prefetch(buttonHref);
  }, [button?.text, buttonHref, navigate]);

  useEffect(() => {
    setYoutubeIframeLoaded(false);
    setShowYoutubeLoadCover(Boolean(youtubeVideoId));
  }, [youtubeVideoId]);

  useEffect(() => {
    if (!youtubeVideoId || !youtubeIframeLoaded) return;

    const timeoutId = window.setTimeout(() => {
      setShowYoutubeLoadCover(false);
    }, YOUTUBE_BACKGROUND_REVEAL_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [youtubeIframeLoaded, youtubeVideoId]);

  return (
    <section
      className="video-hero-section relative w-full overflow-hidden bg-black"
      style={{ '--video-hero-min-h-px': minHeight } as CSSProperties}
    >
      <div className="absolute inset-0">
        {youtubeVideoId ? (
          <>
            <iframe
              src={buildYouTubeBackgroundUrl(youtubeVideoId)}
              title="YouTube background video"
              className={cn(
                'pointer-events-none absolute top-1/2 left-1/2 aspect-video h-auto min-h-full min-w-full border-0 transition-opacity duration-700',
                showYoutubeLoadCover ? 'opacity-0' : 'opacity-100',
              )}
              style={{
                width: youtubeIframeWidth,
                transform: `translate(-50%, -50%) scale(${YOUTUBE_BACKGROUND_SCALE})`,
              }}
              allow="autoplay; encrypted-media; picture-in-picture"
              tabIndex={-1}
              aria-hidden="true"
              onLoad={() => setYoutubeIframeLoaded(true)}
            />
            {showYoutubeLoadCover ? (
              posterImage ? (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${posterImage})` }}
                />
              ) : (
                <div className="absolute inset-0 bg-black" />
              )
            ) : null}
          </>
        ) : directVideoSrc ? (
          <video
            src={directVideoSrc}
            poster={posterImage}
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : posterImage ? (
          <div
            className="h-full w-full bg-cover bg-center"
            style={{ backgroundImage: `url(${posterImage})` }}
          />
        ) : (
          <div className="h-full w-full bg-zinc-900" />
        )}
      </div>

      {resolvedGradient ? (
        <div
          className="pointer-events-none absolute inset-0 z-1"
          style={{ backgroundImage: resolvedGradient }}
        />
      ) : null}

      <div className="relative z-10 flex min-h-[inherit] items-center" style={contentPaddingStyle}>
        <div className="container mx-auto w-full px-5 @lg:px-8">
          <div className="flex max-w-2xl flex-col gap-3 text-left @lg:gap-4">
            {label?.text ? (
              <span
                className={cn(
                  'text-xs tracking-[0.2em] uppercase @lg:text-sm',
                  fontWeightClass(label.fontWeight),
                )}
                style={{
                  color: resolveThemeColor(label.color),
                  fontSize: label.fontSize,
                }}
              >
                {label.text}
              </span>
            ) : null}
            <TitleTag
              className={cn(
                'text-3xl leading-tight @md:text-4xl @lg:text-6xl',
                fontWeightClass(title.fontWeight),
              )}
              style={{ color: resolveThemeColor(title.color), fontSize: title.fontSize }}
            >
              {title.text}
            </TitleTag>
            {subtitle?.text ? (
              <p
                className={cn(
                  'text-lg leading-snug @lg:text-xl',
                  fontWeightClass(subtitle.fontWeight),
                )}
                style={{
                  color: resolveThemeColor(subtitle.color),
                  fontSize: subtitle.fontSize,
                }}
              >
                {subtitle.text}
              </p>
            ) : null}
            {body?.text ? (
              <p
                className={cn(
                  'max-w-xl text-sm leading-relaxed @lg:text-base',
                  fontWeightClass(body.fontWeight),
                )}
                style={{
                  color: resolveThemeColor(body.color),
                  fontSize: body.fontSize,
                }}
              >
                {body.text}
              </p>
            ) : null}
            {button?.text ? (
              <div className="pt-2">
                <Button
                  color="primary"
                  className="min-w-[140px] font-medium"
                  onPress={() => navigate(buttonHref)}
                  onMouseEnter={() => navigate.prefetch(buttonHref)}
                  onFocus={() => navigate.prefetch(buttonHref)}
                  startContent={button.startIcon ? <Icon icon={button.startIcon} /> : undefined}
                  endContent={button.endIcon ? <Icon icon={button.endIcon} /> : undefined}
                >
                  {button.text}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoHeroSection;
